import {
  extractFoodCertRecertifyErrorCode,
  FOOD_CERT_PREVIOUS_ORDER_ALREADY_RENEWED_ERROR,
} from '@aca-new/app/pages/book/pages/food-certification/shared/functions/extract-food-cert-recertify-error-code.function';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EQimaPosition, EQimaSnackbarType, QimaSnackbarService } from '@qima/ngx-qima';

const PREVIOUS_ORDER_ALREADY_LINKED_TO_RENEWAL_KEY = 'BOOKS.FOOD_CERTIFICATION.PREVIOUS_ORDER_ALREADY_LINKED_TO_RENEWAL';
const PREVIOUS_ORDER_ALREADY_RENEWED_KEY = 'BOOKS.FOOD_CERTIFICATION.PREVIOUS_ORDER_ALREADY_RENEWED';

@Injectable({
  providedIn: 'root',
})
export class PreviousOrderLinkedRenewalWarnService {
  public constructor(
    @Optional() private readonly _translateService: TranslateService | null,
    @Optional() private readonly _qimaSnackbarService: QimaSnackbarService | null
  ) {}

  /**
   * When the previous order is already linked to active renewal order(s), shows a warning snackbar.
   * @param {string} orderNumber - Previous order number (QIMA ref) being selected.
   * @param {string[] | null | undefined} linkedRenewalOrderNumbers - Active renewal order numbers from list API.
   * @returns {boolean} True when the selection must be blocked (caller should return early).
   */
  public isShowWarning(orderNumber: string, linkedRenewalOrderNumbers?: string[] | null): boolean {
    if (!orderNumber || !linkedRenewalOrderNumbers?.length) {
      return false;
    }

    if (!this._qimaSnackbarService || !this._translateService) {
      return true;
    }

    this._qimaSnackbarService.open({
      message: this._translateService.instant(PREVIOUS_ORDER_ALREADY_LINKED_TO_RENEWAL_KEY, {
        orderNumber,
        linkedRenewalOrderNumbers: linkedRenewalOrderNumbers.join(', ').trim(),
      }),
      type: EQimaSnackbarType.WARNING,
      position: EQimaPosition.TOP,
      duration: 5000,
    });

    return true;
  }

  /**
   * Shows a user-visible message when recertify API rejects the request.
   * @param {unknown} error - HTTP error from recertify.
   * @param {string} orderNumber - Previous order number (QIMA ref) used for renew.
   * @returns {boolean} True when a known renewal conflict was handled.
   */
  public showRecertifyApiError(error: unknown, orderNumber?: string): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const errorBody = extractFoodCertRecertifyErrorCode(error);

    if (errorBody !== FOOD_CERT_PREVIOUS_ORDER_ALREADY_RENEWED_ERROR) {
      return false;
    }

    if (!this._qimaSnackbarService || !this._translateService) {
      return true;
    }

    this._qimaSnackbarService.open({
      message: this._translateService.instant(PREVIOUS_ORDER_ALREADY_RENEWED_KEY, {
        orderNumber: orderNumber?.trim() ?? '',
      }),
      type: EQimaSnackbarType.WARNING,
      position: EQimaPosition.TOP,
      duration: 5000,
    });

    return true;
  }
}
